"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useMCNFTApprove = exports.useApprove = exports.useNFTApprove = exports.useApproveCallback = exports.useNftTokenApprove = exports.useTokenAllowance = exports.useTokenBalance = exports.ApprovalState = void 0;
const _abi_1 = require("@abi");
const core_1 = require("@web3-react/core");
const hooks_1 = require("@app/store/external/application/hooks");
const hooks_2 = require("@app/store/external/multicall/hooks");
const hooks_3 = require("@app/store/external/transactions/hooks");
const constants_1 = require("@ethersproject/constants");
const object_1 = require("@utils/object");
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const react_1 = require("react");
const contract_hooks_1 = require("./contract.hooks");
const translation_hooks_1 = require("@hooks/translation.hooks");
const tokens_reducer_1 = require("@app/store/reducers/tokens.reducer");
var ApprovalState;
(function (ApprovalState) {
    ApprovalState[ApprovalState["Unknown"] = 0] = "Unknown";
    ApprovalState[ApprovalState["InternalError"] = 1] = "InternalError";
    ApprovalState[ApprovalState["NotApproved"] = 2] = "NotApproved";
    ApprovalState[ApprovalState["Pending"] = 3] = "Pending";
    ApprovalState[ApprovalState["Approved"] = 4] = "Approved";
})(ApprovalState = exports.ApprovalState || (exports.ApprovalState = {}));
const useTokenBalance = (tokenAddress) => {
    const { chainId, account } = (0, core_1.useWeb3React)();
    const contract = (0, contract_hooks_1.useContractWithAddress)("Any20", chainId, tokenAddress);
    const inputs = [account];
    const balance = (0, hooks_2.useSingleCallResult)(contract, "balanceOf", inputs).result;
    const token = (0, tokens_reducer_1.useTokenByAddress)(tokenAddress || "");
    return token
        ? Object.assign(Object.assign({}, token), { balance: new bignumber_js_1.default((balance === null || balance === void 0 ? void 0 : balance.toString()) || 0) }) : undefined;
};
exports.useTokenBalance = useTokenBalance;
const useTokenAllowance = (contract, account, address) => {
    const inputs = [account, address];
    const allowance = (0, hooks_2.useSingleCallResult)(contract, "allowance", inputs).result;
    return allowance === null || allowance === void 0 ? void 0 : allowance.toString();
};
exports.useTokenAllowance = useTokenAllowance;
const useNftTokenApprove = (contract, tokenId) => {
    const approvedFor = (0, hooks_2.useSingleCallResult)(contract, "getApproved", [tokenId]).result;
    return approvedFor === null || approvedFor === void 0 ? void 0 : approvedFor.toString();
};
exports.useNftTokenApprove = useNftTokenApprove;
const useApproveCallback = (spender, tokenAddress, amount) => {
    const { account, chainId } = (0, core_1.useWeb3React)();
    const contract = (0, contract_hooks_1.useContractWithAddress)("Any20", chainId, tokenAddress);
    const [addTransaction, handleTransactionError] = (0, hooks_3.useTransactionAdder)();
    const walletInteractionSetter = (0, hooks_1.useSetWalletInteractions)();
    const currentAllowance = (0, exports.useTokenAllowance)(contract, account, spender);
    const token = (0, tokens_reducer_1.useTokenByAddress)(tokenAddress);
    const pendingApproval = (0, hooks_3.useHasPendingApproval)(contract === null || contract === void 0 ? void 0 : contract.address, spender);
    const translator = (0, translation_hooks_1.useComponentTranslation)("txMessages");
    const [currentApprovalState, setCurrentApprovalState] = (0, react_1.useState)(ApprovalState.Unknown);
    const getState = (amount) => {
        let approvalState = currentApprovalState;
        if (spender && currentAllowance && approvalState !== ApprovalState.Pending) {
            const amountToCheck = amount && Number(amount) ? amount : constants_1.MaxUint256.toString();
            if (new bignumber_js_1.default(currentAllowance).isLessThan(amountToCheck)) {
                approvalState = pendingApproval ? ApprovalState.Pending : ApprovalState.NotApproved;
            }
            else {
                approvalState = ApprovalState.Approved;
            }
        }
        return approvalState;
    };
    const approve = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        const approvalState = getState(amount);
        if (approvalState !== ApprovalState.NotApproved) {
            console.error("approve was called unnecessarily");
            return;
        }
        if (!contract) {
            console.error("tokenContract is null");
            return;
        }
        if (!spender) {
            console.error("no spender");
            return;
        }
        if (currentApprovalState !== ApprovalState.Pending) {
            setCurrentApprovalState(ApprovalState.Pending);
        }
        const summary = translator("tokenApproveMessage", { symbol: token === null || token === void 0 ? void 0 : token.symbol });
        walletInteractionSetter.setPending(summary);
        try {
            const tx = yield contract.approve(spender, amount ? amount : constants_1.MaxUint256.toString());
            addTransaction(tx, {
                summary,
            });
            yield tx.wait(1);
            setCurrentApprovalState(ApprovalState.Unknown);
        }
        catch (e) {
            handleTransactionError(e);
            setCurrentApprovalState(ApprovalState.NotApproved);
            throw e;
        }
    }), [contract, spender, amount, currentAllowance, chainId, currentApprovalState, account]);
    return [getState(amount), approve];
};
exports.useApproveCallback = useApproveCallback;
const useNFTApprove = (address, tokenId, nftAddress) => {
    const { account, chainId } = (0, core_1.useWeb3React)();
    const requestedNftAddress = nftAddress ? _abi_1.ContractManager.getCurrentContractAddress("MCNFT", chainId) : nftAddress;
    const contract = (0, contract_hooks_1.useContractWithAddress)("MCNFT", chainId, requestedNftAddress);
    const [addTransaction, handleTransactionError] = (0, hooks_3.useTransactionAdder)();
    const walletInteractionSetter = (0, hooks_1.useSetWalletInteractions)();
    const approvedAddress = (0, exports.useNftTokenApprove)(contract, tokenId);
    const [currentApprovalState, setCurrentApprovalState] = (0, react_1.useState)(ApprovalState.Unknown);
    const translator = (0, translation_hooks_1.useComponentTranslation)("txMessages");
    const getState = () => {
        let approvalState = currentApprovalState;
        if ((0, object_1.caseInsensitiveCompare)(approvedAddress, address) || approvalState !== ApprovalState.Pending) {
            if ((0, object_1.caseInsensitiveCompare)(approvedAddress, address)) {
                approvalState = ApprovalState.Approved;
            }
            else {
                approvalState = ApprovalState.NotApproved;
            }
        }
        return approvalState;
    };
    const approve = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        const approvalState = getState();
        if (approvalState !== ApprovalState.NotApproved || (0, object_1.caseInsensitiveCompare)(approvedAddress, address)) {
            console.error("approve was called unnecessarily");
            return;
        }
        if (!contract) {
            console.error("tokenContract is null");
            return;
        }
        if (!address) {
            console.error("contract address is empty");
            return;
        }
        if (currentApprovalState !== ApprovalState.Pending) {
            setCurrentApprovalState(ApprovalState.Pending);
        }
        const summary = translator("nftApproveMessage", { tokenId });
        walletInteractionSetter.setPending(summary);
        try {
            const tx = yield contract.approve(address, tokenId);
            addTransaction(tx, {
                summary,
            });
        }
        catch (e) {
            handleTransactionError(e);
            setCurrentApprovalState(ApprovalState.NotApproved);
            throw e;
        }
    }), [contract, tokenId, approvedAddress, account, currentApprovalState]);
    return [getState(), approve];
};
exports.useNFTApprove = useNFTApprove;
const useApprove = (tokenAddress, spender, amount) => (0, exports.useApproveCallback)(spender, tokenAddress, amount === null || amount === void 0 ? void 0 : amount.toFixed(0));
exports.useApprove = useApprove;
const useMCNFTApprove = (tokenId, tokenAddress, contract, callback) => {
    const { chainId } = (0, core_1.useWeb3React)();
    const [approveState, approve] = (0, exports.useNFTApprove)(_abi_1.ContractManager.getCurrentContractAddress(contract, chainId) || "", tokenId, tokenAddress);
    return [
        approveState,
        () => __awaiter(void 0, void 0, void 0, function* () {
            yield approve();
            callback && (yield callback());
        }),
    ];
};
exports.useMCNFTApprove = useMCNFTApprove;
